import type {
  PanderrRenderedTemplate,
  PanderrTemplateDefinition,
  PanderrTemplatePage,
} from "./types";

import { createPanderrBaseCss } from "./base-css";
import { createHeroSectionCss, renderHeroSection } from "./sections/hero";

import {
  createBodyCss,
  renderTrustSection,
  renderServicesSection,
  renderAboutSection,
  renderCtaSection,
  renderProcessSection,
  renderProjectsSection,
  renderTestimonialsSection,
  renderServiceAreaSection,
  renderContactSection,
  renderGallerySection,
  renderFaqSection,
} from "./sections/body";

import {
  createHeaderCss,
  renderPanderrHeader,
  type PanderrHeaderSiteData,
} from "./components/header";

import { createFooterCss, renderPanderrFooter } from "./components/footer";

import {
  createGovernmentHubCss,
  renderGovernmentHubSection,
} from "./sections/government-hub";

import {
  createMunicipalHomeCss,
  renderMunicipalHomeSection,
} from "./sections/municipal-home";

function renderSection(
  section: PanderrTemplateDefinition["sections"][number],
): string {
  switch (section.type) {
    case "hero":
      return renderHeroSection(section);

    case "trust":
      return renderTrustSection(section);

    case "services":
      return renderServicesSection(section);

    case "about":
      return renderAboutSection(section);

    case "cta":
      return renderCtaSection(section);

    case "process":
      return renderProcessSection(section);

    case "projects":
      return renderProjectsSection(section);

    case "testimonials":
      return renderTestimonialsSection(section);

    case "service-area":
      return renderServiceAreaSection(section);

    case "contact":
      return renderContactSection(section);

    case "gallery":
      return renderGallerySection(section);

    case "faq":
      return renderFaqSection(section);

    case "government-hub":
      return renderGovernmentHubSection(section);

    case "municipal-home":
      return renderMunicipalHomeSection(section);

    default:
      return "";
  }
}

function createPanderrTemplateCss(): string {
  return [
    createPanderrBaseCss(),
    createHeaderCss(),
    createHeroSectionCss(),
    createBodyCss(),
    createFooterCss(),
    createGovernmentHubCss(),
    createMunicipalHomeCss(),
  ].join("\n");
}

export function renderPanderrTemplate(
  template: PanderrTemplateDefinition,
): PanderrRenderedTemplate {
  const html = template.sections.map(renderSection).join("\n");

  const css = createPanderrTemplateCss();

  return {
    html: `
      <main
        data-panderr-design-system="v1"
        data-panderr-template-id="${template.id}"
      >
        ${html}
      </main>
    `,
    css,
  };
}

export function renderPanderrTemplatePage(
  template: PanderrTemplateDefinition,
  page: PanderrTemplatePage,
): PanderrRenderedTemplate {
  const html = page.sections.map(renderSection).join("\n");

  return {
    html: `
      <main
        data-panderr-design-system="v1"
        data-panderr-template-id="${template.id}"
        data-panderr-template-page-id="${page.id}"
      >
        ${html}
      </main>
    `,
    css: createPanderrTemplateCss(),
  };
}

function renderManagedContentRuntime(
  template: PanderrTemplateDefinition,
  siteData: PanderrHeaderSiteData,
): string {
  if (template.id !== "municipality-modern") {
    return "";
  }

  const projectId = String(siteData.managedContentProjectId || "").trim();

  if (!projectId) {
    return "";
  }

  const apiBase = String(
    "https://panderr.pages.dev",
  )
    .trim()
    .replace(/\/+$/, "");

  const safeProjectId = JSON.stringify(projectId).replaceAll("<", "\\u003c");
  const safeApiBase = JSON.stringify(apiBase).replaceAll("<", "\\u003c");

  return `
<script data-panderr-managed-content-runtime="true">
(() => {
  const projectId = ${safeProjectId};
  const apiBase = ${safeApiBase};

  const managedImages = [
    {
      sectionId: "departments-hero",
      role: "departments-hero",
    },
  ];

  async function applyManagedImage(binding) {
    const section = document.querySelector(
      '[data-panderr-section-id="' + binding.sectionId + '"]'
    );

    if (!section) return;

    const image = section.querySelector(
      'img[data-panderr-image-role="hero"]'
    );

    if (!image) return;

    try {
      const response = await fetch(
        apiBase +
          "/api/public/projects/" +
          encodeURIComponent(projectId) +
          "/assets/role/" +
          encodeURIComponent(binding.role),
        {
          headers: {
            Accept: "application/json",
          },
        }
      );

      if (!response.ok) return;

      const result = await response.json();
      const asset = result && result.asset;

      if (!asset || !asset.url) return;

      image.src = asset.url;

      if (asset.altText) {
        image.alt = asset.altText;
      }
    } catch (error) {
      console.warn(
        "Panderr managed image could not be loaded:",
        binding.role,
        error
      );
    }
  }

  Promise.all(managedImages.map(applyManagedImage));
})();
</script>`;
}

function escapeHeadValue(value: string): string {
  return String(value || "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function pageSeoTitle(
  pageTitle: string,
  businessName?: string,
): string {
  const page = String(pageTitle || "").trim();
  const business = String(businessName || "").trim();

  if (!business) return page;
  if (!page) return business;

  if (page.toLowerCase() === business.toLowerCase()) {
    return business;
  }

  return `${page} | ${business}`;
}

function defaultSeoDescription(
  siteData: PanderrHeaderSiteData,
): string {
  return (
    siteData.defaultMetaDescription?.trim() ||
    siteData.businessDescription?.trim() ||
    `Official information, services, public records, and community updates from ${
      siteData.businessName?.trim() || "our municipality"
    }.`
  );
}

export function renderPanderrPageDocument(
  template: PanderrTemplateDefinition,
  page: PanderrTemplatePage,
  siteData: PanderrHeaderSiteData = {},
): string {
  const rendered = renderPanderrTemplatePage(template, page);

  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta
    name="viewport"
    content="width=device-width, initial-scale=1"
  >
  <title>${escapeHeadValue(
    pageSeoTitle(page.title, siteData.businessName)
  )}</title>
  <meta
    name="description"
    content="${escapeHeadValue(defaultSeoDescription(siteData))}"
  >
  <meta
    property="og:title"
    content="${escapeHeadValue(
      pageSeoTitle(page.title, siteData.businessName)
    )}"
  >
  <meta
    property="og:description"
    content="${escapeHeadValue(defaultSeoDescription(siteData))}"
  >
  <meta property="og:type" content="website">
  <style>
${rendered.css}
  </style>
</head>
<body data-panderr-template-id="${template.id}">
${renderPanderrHeader(template, siteData)}
${rendered.html}
${renderPanderrFooter(template, siteData)}
${renderManagedContentRuntime(template, siteData)}
</body>
</html>`;
}

export function renderPanderrDocument(
  template: PanderrTemplateDefinition,
  siteData: PanderrHeaderSiteData = {},
): string {
  const rendered = renderPanderrTemplate(template);

  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta
    name="viewport"
    content="width=device-width, initial-scale=1"
  >
  <title>${escapeHeadValue(
    siteData.businessName?.trim() || template.name
  )}</title>
  <meta
    name="description"
    content="${escapeHeadValue(defaultSeoDescription(siteData))}"
  >
  <meta
    property="og:title"
    content="${escapeHeadValue(
      siteData.businessName?.trim() || template.name
    )}"
  >
  <meta
    property="og:description"
    content="${escapeHeadValue(defaultSeoDescription(siteData))}"
  >
  <meta property="og:type" content="website">
  <style>
${rendered.css}
  </style>
</head>
<body data-panderr-template-id="${template.id}">
${renderPanderrHeader(template, siteData)}
${rendered.html}
${renderPanderrFooter(template, siteData)}
${renderManagedContentRuntime(template, siteData)}
</body>
</html>`;
}
